import { useMutation } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Folder, Loader2, Paperclip, X } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { externalApi, type FileUpload } from '@/api/external';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { FormError } from '@/components/ui/form-error';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  type AccountType,
  complementaryDocKeys,
  fieldsByAccountType,
  partnerDirectorDocKey,
} from '@/hooks/commercial/use-credit-analysis-state-machine';
import { useFormError } from '@/hooks/use-form-error';
import { useToast } from '@/hooks/use-toast';
import { fetchWithAuth } from '@/lib/api';

interface PartnerDirector {
  id: string;
  name: string;
  cpf: string;
  document: File | null;
}

interface DocumentSlotState {
  fieldId: string;
  label: string;
  required: boolean;
  status: 'approved' | 'rejected' | 'pending' | 'none';
  rejectionReason?: string;
  uploadedFileName?: string;
  uploadedAt?: string;
}

interface ExternalDocumentUploadFormProps {
  linkId: string;
  email: string;
  accountType: AccountType;
  documentSlots: DocumentSlotState[];
  onSuccess?: () => void;
}

const getFileExtension = (fileName: string) => {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts.pop() : '';
};

export function ExternalDocumentUploadForm({
  linkId,
  email,
  accountType,
  documentSlots,
  onSuccess,
}: ExternalDocumentUploadFormProps) {
  const { formError, showFormError, clearFormError } = useFormError();
  const [documentError, setDocumentError] = useState<string | null>(null);
  const { toast } = useToast();

  const [files, setFiles] = useState<Map<string, File>>(new Map());
  const [partnersDirectors, setPartnersDirectors] = useState<PartnerDirector[]>([]);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [isUploading, setIsUploading] = useState(false);

  // Get current fields (excluding partner/director and complementary docs)
  const currentFields = useMemo(
    () =>
      fieldsByAccountType[accountType].filter(
        (field) =>
          field.key !== partnerDirectorDocKey && !complementaryDocKeys.includes(field.key as any),
      ),
    [accountType],
  );

  // Get complementary fields (for ME)
  const complementaryFields = useMemo(
    () =>
      accountType === 'ME'
        ? fieldsByAccountType[accountType].filter((field) =>
            complementaryDocKeys.includes(field.key as any),
          )
        : [],
    [accountType],
  );

  // Create a map of document slot states for quick lookup
  const slotStateMap = useMemo(() => {
    const map = new Map<string, DocumentSlotState>();
    documentSlots.forEach((slot) => {
      map.set(slot.fieldId, slot);
    });
    return map;
  }, [documentSlots]);

  // Combined mutation for uploading all documents
  const uploadDocumentsMutation = useMutation({
    mutationFn: async (): Promise<FileUpload[]> => {
      // Collect all files to upload
      const filesToUpload: Array<{
        fieldId: string;
        file: File;
        fileName: string;
        contentType: string;
      }> = [];

      // Add main documents
      Array.from(files.entries()).forEach(([key, file]) => {
        filesToUpload.push({
          fieldId: key,
          file,
          fileName: file.name,
          contentType: file.type || 'application/octet-stream',
        });
      });

      // Add partner/director documents
      partnersDirectors.forEach((partner) => {
        if (partner.document) {
          filesToUpload.push({
            fieldId: `partner-doc-${partner.id}`,
            file: partner.document,
            fileName: partner.document.name,
            contentType: partner.document.type || 'application/octet-stream',
          });
        }
      });

      // Generate signed URLs and upload all files to S3
      const uploadPromises = filesToUpload.map(async (fileInfo) => {
        setUploadProgress((prev) => ({ ...prev, [fileInfo.fieldId]: 10 }));

        // Get signed URL from S3
        const response = await fetchWithAuth('/api/upload/s3/sign', {
          method: 'POST',
          body: JSON.stringify({
            key: `external/${linkId}/${fileInfo.fieldId}.${getFileExtension(fileInfo.fileName)}`,
            contentType: fileInfo.contentType,
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to get signed URL for ${fileInfo.fileName}`);
        }

        const json = await response.json();

        const uploadUrl = json.data?.uploadUrl;
        const key = json.data?.key;

        if (!uploadUrl || !key) {
          throw new Error('Invalid signed URL response');
        }

        setUploadProgress((prev) => ({ ...prev, [fileInfo.fieldId]: 30 }));

        // Upload to S3
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: fileInfo.file,
          headers: { 'Content-Type': fileInfo.contentType },
        });

        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload ${fileInfo.fileName} to S3`);
        }

        const eTag = uploadResponse.headers.get('ETag') || undefined;

        setUploadProgress((prev) => ({ ...prev, [fileInfo.fieldId]: 70 }));

        return {
          fieldId: fileInfo.fieldId,
          fileName: fileInfo.fileName,
          key,
          sizeInBytes: fileInfo.file.size,
          contentType: fileInfo.contentType,
          eTag,
        };
      });

      const uploadedResults = await Promise.all(uploadPromises);

      // Mark all as uploaded
      setUploadProgress((prev) => {
        const newProgress = { ...prev };
        Object.keys(newProgress).forEach((key) => {
          newProgress[key] = 100;
        });
        return newProgress;
      });

      return uploadedResults;
    },
    onSuccess: async (uploadedFiles) => {
      // Send metadata to backend
      await externalApi.uploadDocuments(linkId, email, uploadedFiles);

      toast({
        variant: 'success',
        title: 'Documentos enviados',
        description: 'Os documentos foram enviados com sucesso para análise!',
      });

      setFiles(new Map());
      setPartnersDirectors([]);
      setUploadProgress({});

      onSuccess?.();
    },
    onError: (error) => {
      console.error('Error uploading documents:', error);
      showFormError(error);
      setIsUploading(false);
      setUploadProgress({});
    },
  });

  const handleFileChange = useCallback((key: string, file: File | null) => {
    setFiles((prev) => {
      const newMap = new Map(prev);
      if (file) {
        newMap.set(key, file);
      } else {
        newMap.delete(key);
      }
      return newMap;
    });
  }, []);

  const handlePartnerDirectorChange = useCallback(
    (id: string, field: keyof PartnerDirector, value: string | File | null) => {
      setPartnersDirectors((prev) =>
        prev.map((pd) => (pd.id === id ? { ...pd, [field]: value } : pd)),
      );
    },
    [],
  );

  const addPartnerDirector = useCallback(() => {
    setPartnersDirectors((prev) => [
      ...prev,
      { id: `partner-${Date.now()}`, name: '', cpf: '', document: null },
    ]);
  }, []);

  const removePartnerDirector = useCallback((id: string) => {
    setPartnersDirectors((prev) => prev.filter((pd) => pd.id !== id));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setDocumentError(null);

    // Check if at least one file is selected
    const hasFiles = files.size > 0;
    const hasPartnerDocs = partnersDirectors.some((p) => p.document !== null);

    if (!hasFiles && !hasPartnerDocs) {
      setDocumentError('Selecione pelo menos um documento para enviar.');
      return;
    }

    setIsUploading(true);
    try {
      await uploadDocumentsMutation.mutateAsync();
    } finally {
      setIsUploading(false);
    }
  };

  const renderFileSlot = (field: { key: string; label: string; required: boolean }) => {
    const slotState = slotStateMap.get(field.key);
    const selectedFile = files.get(field.key);
    const progress = uploadProgress[field.key];

    // Field is required only if:
    // - It's required by default AND
    // - Status is NOT 'approved' (already approved, no need to resend) AND
    // - Status is NOT 'pending' (already submitted, waiting for review)
    const isRequired =
      field.required && slotState?.status !== 'approved' && slotState?.status !== 'pending';

    // Approved slot - read-only, grayed out
    if (slotState?.status === 'approved') {
      return (
        <div key={field.key} className="space-y-1">
          <div className="flex items-center justify-between gap-4 px-4 py-3 border border-input rounded-md bg-muted/50 opacity-60">
            <Label className="flex-1 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              {field.label}
            </Label>
            <span className="text-sm text-green-700">Aprovado</span>
          </div>
          {slotState.uploadedFileName && (
            <div className="mt-2 flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 shadow-sm">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-green-100 text-green-700">
                <Folder className="h-5 w-5" />
              </div>
              <div className="flex flex-col flex-1">
                <span className="font-medium text-foreground">{field.label}</span>
                <span className="text-sm text-muted-foreground">{slotState.uploadedFileName}</span>
              </div>
            </div>
          )}
        </div>
      );
    }

    // Rejected slot - red border, show rejection reason
    if (slotState?.status === 'rejected') {
      return (
        <div key={field.key} className="space-y-1">
          <div className="flex items-center justify-between gap-4 px-4 py-3 border border-red-300 rounded-md bg-red-50">
            <Label htmlFor={field.key} className="flex-1 cursor-pointer text-red-900">
              <AlertCircle className="h-4 w-4 inline mr-2 text-red-600" />
              {field.label}
              {isRequired && <span className="text-red-600 ml-1">*</span>}
            </Label>
            <Button
              variant="outline"
              size="sm"
              className="gap-2 shrink-0 border-red-300 text-red-700 hover:bg-red-100"
              type="button"
              onClick={() => {
                document.getElementById(field.key)?.click();
              }}
              disabled={isUploading}
            >
              <Paperclip className="h-4 w-4" />
              Anexar
            </Button>
            <input
              type="file"
              id={field.key}
              name={field.key}
              required={isRequired}
              onChange={(e) => {
                const selectedFile = e.target.files?.[0] || null;
                handleFileChange(field.key, selectedFile);
              }}
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png"
              disabled={isUploading}
            />
          </div>

          {/* Rejection reason */}
          {slotState.rejectionReason && (
            <Alert variant="destructive" className="mt-2">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Motivo do Indeferimento:</strong> {slotState.rejectionReason}
              </AlertDescription>
            </Alert>
          )}

          {/* Previously uploaded file info */}
          {slotState.uploadedFileName && (
            <div className="mt-2 text-sm text-muted-foreground px-2">
              Arquivo anterior: {slotState.uploadedFileName}
            </div>
          )}

          {/* Upload Progress */}
          {isUploading && progress !== undefined && progress > 0 && progress < 100 && (
            <div className="mt-2">
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Newly selected file */}
          {selectedFile && (
            <div className="mt-2 flex items-center gap-3 rounded-lg border border-sky-100 bg-sky-50 px-4 py-3 shadow-sm">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-sky-100 text-sky-700">
                <Folder className="h-5 w-5" />
              </div>
              <div className="flex flex-col flex-1">
                <span className="font-medium text-foreground">{field.label}</span>
                <span className="text-sm text-muted-foreground">
                  {selectedFile.name}
                  {getFileExtension(selectedFile.name)
                    ? ` • ${getFileExtension(selectedFile.name)}`
                    : ''}
                </span>
              </div>
              {!isUploading && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => handleFileChange(field.key, null)}
                  className="h-8 w-8 text-red-500 hover:text-red-700"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
        </div>
      );
    }

    // Pending/None slot - normal upload slot
    return (
      <div key={field.key} className="space-y-1">
        <div className="flex items-center justify-between gap-4 px-4 py-3 border border-input rounded-md bg-background">
          <Label htmlFor={field.key} className="flex-1 cursor-pointer">
            {field.label}
            {isRequired && <span className="text-destructive ml-1">*</span>}
          </Label>
          <Button
            variant="outline"
            size="sm"
            className="gap-2 shrink-0"
            type="button"
            onClick={() => {
              document.getElementById(field.key)?.click();
            }}
            disabled={isUploading}
          >
            <Paperclip className="h-4 w-4" />
            Anexar
          </Button>
          <input
            type="file"
            id={field.key}
            name={field.key}
            required={isRequired}
            onChange={(e) => {
              const selectedFile = e.target.files?.[0] || null;
              handleFileChange(field.key, selectedFile);
            }}
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png"
            disabled={isUploading}
          />
        </div>

        {/* Upload Progress */}
        {isUploading && progress !== undefined && progress > 0 && progress < 100 && (
          <div className="mt-2">
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Selected file display */}
        {selectedFile && (
          <div className="mt-2 flex items-center gap-3 rounded-lg border border-sky-100 bg-sky-50 px-4 py-3 shadow-sm">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-sky-100 text-sky-700">
              <Folder className="h-5 w-5" />
            </div>
            <div className="flex flex-col flex-1">
              <span className="font-medium text-foreground">{field.label}</span>
              <span className="text-sm text-muted-foreground">
                {selectedFile.name}
                {getFileExtension(selectedFile.name)
                  ? ` • ${getFileExtension(selectedFile.name)}`
                  : ''}
              </span>
            </div>
            {!isUploading && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => handleFileChange(field.key, null)}
                className="h-8 w-8 text-red-500 hover:text-red-700"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}

        {/* Previously uploaded file info (for pending) */}
        {slotState?.status === 'pending' && slotState.uploadedFileName && !selectedFile && (
          <div className="mt-2 flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 shadow-sm">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-blue-100 text-blue-700">
              <Folder className="h-5 w-5" />
            </div>
            <div className="flex flex-col flex-1">
              <span className="font-medium text-foreground">{field.label}</span>
              <span className="text-sm text-blue-700">
                Em análise: {slotState.uploadedFileName}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <form noValidate onSubmitCapture={clearFormError} className="space-y-6" onSubmit={handleSubmit}>
      {/* Main Documents */}
      <div className="space-y-3">{currentFields.map((field) => renderFileSlot(field))}</div>

      {/* Partners/Directors Section (for ME) */}
      {accountType === 'ME' && (
        <>
          <div className="space-y-4 mt-6">
            <h3 className="text-lg font-semibold">Sócio/Diretor</h3>
            {partnersDirectors.map((partner, index) => {
              const partnerFieldKey = `partner-doc-${partner.id}`;
              const progress = uploadProgress[partnerFieldKey];

              return (
                <div
                  key={partner.id}
                  className="space-y-3 p-4 border border-input rounded-md bg-muted/30"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor={`partner-name-${partner.id}`}>
                        Nome do Sócio/Diretor
                        <span className="text-destructive ml-1">*</span>
                      </Label>
                      <Input
                        id={`partner-name-${partner.id}`}
                        value={partner.name}
                        onChange={(e) =>
                          handlePartnerDirectorChange(partner.id, 'name', e.target.value)
                        }
                        required
                        disabled={isUploading}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`partner-cpf-${partner.id}`}>
                        CPF
                        <span className="text-destructive ml-1">*</span>
                      </Label>
                      <Input
                        id={`partner-cpf-${partner.id}`}
                        value={partner.cpf}
                        onChange={(e) =>
                          handlePartnerDirectorChange(partner.id, 'cpf', e.target.value)
                        }
                        required
                        disabled={isUploading}
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center justify-between gap-4 px-4 py-3 border border-input rounded-md bg-background">
                      <Label
                        htmlFor={`partner-doc-${partner.id}`}
                        className="flex-1 cursor-pointer"
                      >
                        Documento de identificação (RG/CIN/CNH/Passaporte)
                        <span className="text-destructive ml-1">*</span>
                      </Label>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2 shrink-0"
                        type="button"
                        onClick={() => {
                          document.getElementById(`partner-doc-${partner.id}`)?.click();
                        }}
                        disabled={isUploading}
                      >
                        <Paperclip className="h-4 w-4" />
                        Anexar
                      </Button>
                      <input
                        type="file"
                        id={`partner-doc-${partner.id}`}
                        required
                        onChange={(e) => {
                          const selectedFile = e.target.files?.[0] || null;
                          handlePartnerDirectorChange(partner.id, 'document', selectedFile);
                        }}
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png"
                        disabled={isUploading}
                      />
                    </div>

                    {/* Upload Progress for partner doc */}
                    {isUploading && progress !== undefined && progress > 0 && progress < 100 && (
                      <div className="mt-2">
                        <div className="w-full bg-muted rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Partner doc selected */}
                    {partner.document && (
                      <div className="mt-2 flex items-center gap-3 rounded-lg border border-sky-100 bg-sky-50 px-4 py-3 shadow-sm">
                        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-sky-100 text-sky-700">
                          <Folder className="h-5 w-5" />
                        </div>
                        <div className="flex flex-col flex-1">
                          <span className="font-medium text-foreground">
                            Documento: {partner.name}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {partner.document.name}
                            {getFileExtension(partner.document.name)
                              ? ` • ${getFileExtension(partner.document.name)}`
                              : ''}
                          </span>
                        </div>
                        {!isUploading && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              handlePartnerDirectorChange(partner.id, 'document', null)
                            }
                            className="h-8 w-8 text-red-500 hover:text-red-700"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>

                  {index === partnersDirectors.length - 1 && (
                    <div className="flex gap-2 justify-end">
                      {partnersDirectors.length > 1 && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-2"
                          type="button"
                          onClick={() => removePartnerDirector(partner.id)}
                          disabled={isUploading}
                        >
                          <X className="h-4 w-4" />
                          Remover
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        type="button"
                        onClick={addPartnerDirector}
                        disabled={isUploading}
                      >
                        <Paperclip className="h-4 w-4" />
                        Adicionar Sócio/Diretor
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Add first partner/director button */}
            {partnersDirectors.length === 0 && (
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                type="button"
                onClick={addPartnerDirector}
                disabled={isUploading}
              >
                <Paperclip className="h-4 w-4" />
                Adicionar Sócio/Diretor
              </Button>
            )}
          </div>

          {/* Complementary Documents (for ME) */}
          <div className="space-y-4 mt-6">
            <h3 className="text-lg font-semibold">Documentos Complementares</h3>
            <div className="space-y-3">
              {complementaryFields.map((field) => renderFileSlot(field))}
            </div>
          </div>
        </>
      )}

      {/* Actions - Single batch upload button */}
      <p className="text-sm text-red-500">{documentError}</p>
      <FormError error={formError} onDismiss={clearFormError} />
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button type="submit" disabled={isUploading || files.size === 0} className="gap-2">
          {isUploading && <Loader2 className="h-4 w-4 animate-spin" />}
          {isUploading ? 'Enviando documentos...' : 'Enviar Documentos para Análise'}
        </Button>
      </div>
    </form>
  );
}
